import * as React from "react";
import { cn } from "../../lib/utils";
import { Tooltip } from "./Tooltip";

/**
 * The truncation contract.
 *
 *   TABLE CELL   one line, then an ellipsis.
 *   CARD VALUE   two lines, then an ellipsis.
 *
 * 🔴 AN ELLIPSIS IS ALWAYS PRESENT WHEN A VALUE IS CLAMPED. A cut with no mark
 * is not truncation, it is a wrong value: a live buyer email currently stops
 * mid-string with nothing to say it did, so the reader has no way to know
 * whether they are looking at the address or at the first two thirds of it. The
 * defect is `overflow: hidden` used alone — it hides the overflow and the fact
 * that there was any. `text-overflow: ellipsis` (single line) and
 * `-webkit-line-clamp` (multi-line) both mark the cut; only these two are used
 * here, and never a bare `overflow-hidden`.
 *
 * 🔴 THE FULL VALUE IS AVAILABLE ON HOVER **AND ON KEYBOARD FOCUS.** A clamped
 * value with a mouse-only reveal is unreadable to anyone driving the page from
 * the keyboard. That is why this composes {@link Tooltip} rather than setting a
 * native `title`: `title` never opens on focus, so it satisfies the hover half
 * of the contract and silently fails the other. There is exactly one tooltip in
 * this app and this is it — do not add a second, and do not fall back to
 * `title`.
 *
 * (`StatusBadge.hint` still renders through `title`. That is a known gap, in
 * flight across four external portals, and explicitly NOT a precedent.)
 *
 * WHY THE TOOLTIP IS ATTACHED BY DEFAULT. Whether a given string overflows
 * depends on the font, the container and the zoom level, so "attach the reveal
 * only when it actually clamps" means measuring after layout and re-measuring
 * on resize. In a TABLE that buys little — almost every cell clamps — and being
 * wrong in the safe direction costs a reader a hint they did not need.
 *
 * 🔴 ON A RECORD CARD THE SAME DEFAULT IS A KEYBOARD REGRESSION, WHICH IS WHY
 * `revealOnlyWhenClamped` NOW EXISTS RATHER THAN BEING DESCRIBED. This comment
 * named the prop and the file never had one: it was documented, cited, and
 * absent, so a caller reaching for it got no reveal control and no error. It is
 * implemented below, opt-in, measured in a layout effect and kept honest by a
 * `ResizeObserver` — see the prop's own note for what the measurement found.
 */

export type TruncateLines = 1 | 2 | 3;

/** One literal per clamp — Tailwind emits only class names it can see verbatim. */
const CLAMP_CLASS: Record<TruncateLines, string> = {
  // `truncate` is overflow-hidden + text-overflow: ellipsis + nowrap.
  1: "truncate",
  2: "line-clamp-2",
  3: "line-clamp-3",
};

export interface TruncateProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** The full value. Also the text revealed on hover / focus. */
  children: string;
  /** How many lines before the ellipsis. 1 for a table cell, 2 for a card value. */
  lines?: TruncateLines;
  /** Suppress the hover/focus reveal (rare — the value is genuinely short). */
  reveal?: boolean;
  /**
   * Attach the reveal only once the value is MEASURED to overflow.
   *
   * <p>🔴 <b>The tab stop is the cost, and on a dense surface it is paid many
   * times for nothing.</b> The reveal is always-on by default because in a
   * TABLE almost every cell clamps, so measuring would buy little and cost a
   * re-measure per render. A record card is the opposite: measured on the
   * buyer and listing Overviews at their production card width, three cards
   * carried 18 newly-focusable values and <b>none of them was clamped</b> —
   * eighteen tab stops that reveal nothing, and roughly forty on a full
   * Overview. Opt in here and a value becomes focusable only when there is
   * something behind the ellipsis to reach.</p>
   *
   * <p>Opt-in, so every existing consumer renders exactly what it rendered
   * before. The measurement runs in a layout effect (before paint, so there is
   * no frame in which a clamped value is unreachable) and repeats through a
   * `ResizeObserver`, which is what answers the standing objection to measuring
   * — "wrong in the window between" — rather than leaving it standing.</p>
   *
   * <p>⚠️ jsdom has no layout, so `scrollHeight` and `clientHeight` are both 0
   * and nothing ever measures as clamped. A component test therefore sees the
   * un-revealed shape; assert the clamp class and the complete DOM text, which
   * are the parts jsdom can actually see.</p>
   */
  revealOnlyWhenClamped?: boolean;
}

export function Truncate({
  children,
  lines = 1,
  reveal = true,
  revealOnlyWhenClamped = false,
  className,
  ...props
}: TruncateProps) {
  const text = children ?? "";
  const ref = React.useRef<HTMLSpanElement | null>(null);
  const [overflows, setOverflows] = React.useState(false);

  React.useLayoutEffect(() => {
    if (!revealOnlyWhenClamped) return;
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      // 🔴 THE MEASUREMENT MUST NOT ANSWER FOR A BOX THAT NO LONGER EXISTS.
      // Turning the reveal ON changes what this component returns — a bare
      // span becomes a `Tooltip`-wrapped one — and React reconciles a `span`
      // against a `Fragment` at that position by REMOUNTING. The old span is
      // then detached, reports 0x0, and its still-live observer writes `false`
      // straight back over the `true` that caused the remount. Measured before
      // this guard: `338/307 true` -> `0/0 false` -> settle FALSE, so a
      // genuinely clipped email never became reachable and the whole feature
      // was inert while looking implemented. Both conditions matter — a
      // detached node and a not-yet-laid-out one are different states and
      // neither has an answer worth writing.
      if (!el.isConnected) return;
      if (el.clientWidth === 0 && el.clientHeight === 0) return;
      setOverflows(
        el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1,
      );
    };

    // The first pass runs during commit, when an ancestor grid may not have
    // resolved its tracks yet — measured, the same email reported 388/388 here
    // and 338/225 one frame later. It is kept because it is right whenever the
    // layout IS settled (a re-render of a mounted tree), and the observer
    // below is what covers the case where it is not.
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [revealOnlyWhenClamped, text, lines]);

  const revealed = reveal && (!revealOnlyWhenClamped || overflows);

  const body = (
    <span
      className={cn("block min-w-0", CLAMP_CLASS[lines], className)}
      data-phx-truncate={lines}
      // Focusable so a keyboard reader can reach the reveal at all. Without it
      // the tooltip's focus half has nothing to fire on — a `<span>` is not in
      // the tab order, so "and on keyboard focus" would be a comment rather
      // than a behaviour.
      //
      // The lint rule is right in general and wrong here. Its own suggested
      // shapes do not fit: `role="button"` announces an action that does not
      // exist, and there is no ARIA role for "text that reveals its full self".
      // The people this serves are SIGHTED KEYBOARD users — a screen reader
      // already gets the whole string, because the clamp is CSS and the DOM
      // text is complete. So the cost is one extra tab stop and the benefit is
      // the only way that group can read a clamped value at all.
      //
      // Disabled per occurrence, on purpose. `GlossaryHint` and `StatusStepper`
      // have the identical need and are switched off per FILE in
      // eslint.config.js as a tracked baseline; this does not join that list.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={revealed ? 0 : undefined}
      ref={ref}
      {...props}
    >
      {text}
    </span>
  );

  if (!revealed || text.length === 0) return body;
  return <Tooltip label={text}>{body}</Tooltip>;
}

/**
 * A table cell's value: one line, ellipsis, revealed on hover and focus.
 *
 * The cell itself must be able to shrink for the clamp to ever engage — a
 * `<td>` sizes to its content by default. `min-w-0` here is what lets the
 * column give way instead of the table growing a horizontal scrollbar.
 */
export function TruncateCell({ children, className, ...props }: Omit<TruncateProps, "lines">) {
  return (
    <Truncate lines={1} className={cn("max-w-full", className)} {...props}>
      {children}
    </Truncate>
  );
}

/** A card's value: two lines, then an ellipsis. */
export function TruncateValue({ children, className, ...props }: Omit<TruncateProps, "lines">) {
  return (
    <Truncate lines={2} className={className} {...props}>
      {children}
    </Truncate>
  );
}
